"use client"

import { useState } from "react"
import { Table, Badge, Button, Form, InputGroup } from "react-bootstrap"
import moment from "moment"

const CropTable = ({ crops, onCropSelect, selectedCrop, showSeasonalBadge = false }) => {
  const [sortField, setSortField] = useState("name")
  const [sortDirection, setSortDirection] = useState("asc")
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  if (!crops || crops.length === 0) {
    return (
      <div className="text-center py-4 text-muted">
        <p>No suitable crops found for this location and time period.</p>
        <small>Try selecting a different location or adjusting the weather analysis period.</small>
      </div>
    )
  }

  // Filter crops based on search term
  const filteredCrops = crops.filter(
    (crop) =>
      crop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (crop.soil_type && crop.soil_type.toLowerCase().includes(searchTerm.toLowerCase())),
  )

  // Sort crops
  const sortedCrops = [...filteredCrops].sort((a, b) => {
    let aValue = a[sortField]
    let bValue = b[sortField]

    if (sortField === "startDate" || sortField === "endDate") {
      aValue = new Date(aValue)
      bValue = new Date(bValue)
    }

    if (typeof aValue === "string") {
      aValue = aValue.toLowerCase()
      bValue = bValue.toLowerCase()
    }

    if (sortDirection === "asc") {
      return aValue > bValue ? 1 : -1
    } else {
      return aValue < bValue ? 1 : -1
    }
  })

  const totalPages = Math.ceil(sortedCrops.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentCrops = sortedCrops.slice(startIndex, endIndex)

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const formatDate = (dateString) => {
    return moment(dateString).format("MMM DD")
  }

  const getSortIcon = (field) => {
    if (sortField !== field) return "↕️"
    return sortDirection === "asc" ? "↑" : "↓"
  }

  return (
    <div>
      {/* Search and Filter */}
      <div className="mb-3">
        <InputGroup>
          <Form.Control
            type="text"
            placeholder="Search crops by name or soil type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
        <small className="text-muted">
          Showing {startIndex + 1}-{Math.min(endIndex, sortedCrops.length)} of {sortedCrops.length} suitable crops
          {onCropSelect && " • Click on a crop row for detailed hourly analysis"}
        </small>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <Table striped hover>
          <thead className="table-success">
            <tr>
              <th style={{ width: "80px" }}>Photo</th>
              <th>
                <Button
                  variant="link"
                  className="p-0 text-dark fw-bold text-decoration-none"
                  onClick={() => handleSort("name")}
                >
                  Crop Name {getSortIcon("name")}
                </Button>
              </th>
              <th>
                <Button
                  variant="link"
                  className="p-0 text-dark fw-bold text-decoration-none"
                  onClick={() => handleSort("startDate")}
                >
                  Start Date {getSortIcon("startDate")}
                </Button>
              </th>
              <th>
                <Button
                  variant="link"
                  className="p-0 text-dark fw-bold text-decoration-none"
                  onClick={() => handleSort("endDate")}
                >
                  End Date {getSortIcon("endDate")}
                </Button>
              </th>
              <th>Temperature Range</th>
              <th>
                <Button
                  variant="link"
                  className="p-0 text-dark fw-bold text-decoration-none"
                  onClick={() => handleSort("growth_period_days")}
                >
                  Growth Period {getSortIcon("growth_period_days")}
                </Button>
              </th>
              <th>Soil Type</th>
              <th>
                <Button
                  variant="link"
                  className="p-0 text-dark fw-bold text-decoration-none"
                  onClick={() => handleSort("suitabilityScore")}
                >
                  Suitability {getSortIcon("suitabilityScore")}
                </Button>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentCrops.map((crop, index) => (
              <tr
                key={index}
                onClick={() => onCropSelect && onCropSelect(crop)}
                className={`${onCropSelect ? "cursor-pointer" : ""} ${selectedCrop?.id === crop.id ? "table-primary" : ""}`}
                style={{ cursor: onCropSelect ? "pointer" : "default" }}
              >
                <td>
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid #ddd",
                      backgroundColor: "#f8f9fa",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {crop.photo ? (
                      <img
                        src={
                          crop.photo.startsWith("http")
                            ? crop.photo
                            : `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8787"}/api/photos/${crop.photo}`
                        }
                        alt={crop.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.src = "/placeholder.svg?key=1osll"
                        }}
                      />
                    ) : (
                      <img
                        src={`/abstract-geometric-shapes.png?key=owrtp&key=a5ryg&key=kzywl&height=60&width=60&query=${encodeURIComponent(crop.name + " crop")}`}
                        alt={`${crop.name} placeholder`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          opacity: 0.6,
                        }}
                      />
                    )}
                  </div>
                </td>
                <td className="fw-medium">
                  {crop.name}
                  {showSeasonalBadge && crop.isCurrentSeason && (
                    <Badge bg="success" className="ms-2 small">
                      Current Season
                    </Badge>
                  )}
                </td>
                <td>{formatDate(crop.startDate)}</td>
                <td>{formatDate(crop.endDate)}</td>
                <td>
                  <Badge bg="primary" className="border text-white">
                    {crop.min_temp}° - {crop.max_temp}°C
                  </Badge>
                </td>
                <td>{crop.growth_period_days} days</td>
                <td>
                  {crop.soil_type && (
                    <Badge bg="secondary" className="rounded-pill">
                      {crop.soil_type}
                    </Badge>
                  )}
                </td>
                <td>
                  <Badge
                    bg={crop.suitabilityScore >= 80 ? "success" : crop.suitabilityScore >= 60 ? "warning" : "info"}
                    className="rounded-pill"
                  >
                    {crop.suitabilityScore || 85}%
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <div className="d-flex gap-1">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ‹ Previous
            </Button>

            {[...Array(totalPages)].map((_, i) => (
              <Button
                key={i + 1}
                variant={currentPage === i + 1 ? "primary" : "outline-primary"}
                size="sm"
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </Button>
            ))}

            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next ›
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CropTable
